import {
	EntityCollectionServiceBase,
	EntityCollectionServiceElementsFactory,
	EntityServices,
} from "@ngrx/data";
import { NorthstarQueryParams, RecordTypeName } from "@recore-solutions/nstar-lib";
import { combineLatest, Observable } from "rxjs";
import { map } from "rxjs/operators";

/**
 * This is a wrapper for ngrx's EntityCollectionServiceBase that is primarily intended to enforce agreement between the type <T> and the entity name (RecordTypeName<T>)
 *
 */
export class PolarisEntityCollectionServiceBase<T> extends EntityCollectionServiceBase<T> {
	constructor(
		recordTypeName: RecordTypeName<T>,
		serviceElementsFactory: EntityCollectionServiceElementsFactory,
		entityServices?: EntityServices
	) {
		super(recordTypeName, serviceElementsFactory);
		if (!!entityServices) {
			entityServices.registerEntityCollectionService<T>(this);
		}
	}

	public search = (
		pattern: any,
		recordCount?: string,
		additionalParams?: Partial<NorthstarQueryParams>,
		searchColumn: string = "FullTextSearch"
	): Observable<T[]> => {
		const queryParams: Partial<NorthstarQueryParams> = {
			...(additionalParams || {}),
			[searchColumn]: "%" + (pattern || "").split(" ").join("%") + "%",
			pageSize: +(recordCount || 25),
		};
		return this.getWithQuery(queryParams);
	};

	/**
	 * Builds a custom search function based upon the column to search and additional parameters to limit the search.
	 */
	public customSearchFnFactory =
		(searchColumn: string) =>
		(additionalParams: Partial<NorthstarQueryParams> | Partial<NorthstarQueryParams>[]) =>
		(pattern: any, recordCount?: string) => {
			const additionalParamsArray: Partial<NorthstarQueryParams>[] = (
				Array.isArray(additionalParams) ? additionalParams : [additionalParams]
			).filter((x) => Object.keys(x).length > 0);
			return combineLatest(
				additionalParamsArray.map((params) =>
					this.search(pattern, recordCount, params, searchColumn)
				)
			).pipe(map(this.reduceArrayOfArrays));
		};

	reduceArrayOfArrays = (arr: T[][]) => arr.reduce((a: T[], c: T[]): T[] => a.concat(c));
}
